import { Product } from "@/types/product";
import { supabase } from "@/integrations/supabase/client";
import { MockDataService } from "@/services/mock/MockDataService";
import { csvProductService } from "@/services/csv/CsvProductService";
import { logger } from "@/services/logger";
import { ErpOrderRequest } from "@/types/order";

interface GetProductsParams {
  page?: number;
  itemsPerPage?: number;
  search?: string;
  categoryIds?: string[];
}

interface GetProductsResponse {
  products: Product[];
  total: number;
  currentPage: number;
  totalPages: number;
}

interface GetOrdersParams {
  customerId?: string;
  contactId?: number;
  page?: number;
  itemsPerPage?: number;
  search?: string;
}

interface OrderSummary {
  id: string;
  orderNumber: string;
  date: string;
  status: string;
  totalAmount: number;
  itemCount: number;
  items: Array<{
    name: string;
    quantity: number;
    sku: string;
  }>;
  customerId?: string;
}

interface OrderDetails {
  id: string;
  orderNumber: string;
  date: string;
  status: string;
  totalAmount: number;
  customerId?: string;
  shippingAddress: {
    company: string;
    contactPerson: string;
    address1: string;
    address2: string;
    postalCode: string;
    city: string;
    country: string;
  };
  items: Array<{
    name: string;
    sku: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    color: string;
    size: string;
  }>;
  estimatedDelivery: string;
  deliveredDate?: string | null;
}

interface GetOrdersResponse {
  orders: OrderSummary[];
  total: number;
  currentPage: number;
  totalPages: number;
}

interface CsvFetchResponse {
  data: unknown[];
  timestamp: string;
  source: 'cache' | 'fresh' | 'expired_cache';
  recordCount: number;
  error?: string;
}

class PlentyOneApiService {
  async getProducts(params: GetProductsParams = {}): Promise<GetProductsResponse> {
    const { page = 1, itemsPerPage = 12, search = "", categoryIds = [] } = params;
    // Get current language from localStorage or default to 'en'
    const lang = localStorage.getItem('knitido_user_language') || 'en';
    
    // Use product cache API if enabled via environment variable
    const useProductCache = import.meta.env.VITE_USE_PRODUCT_CACHE === 'true';
    const endpoint = useProductCache ? 'products-cache-api' : 'plenty-one-api';
    
    logger.debug(`PlentyOneApiService: Fetching ${useProductCache ? 'cached products' : 'CSV data from ERP URL'} via Edge Function...`);
    
    try {
      // Use appropriate Edge Function based on environment configuration
      // Both APIs now use the same request format for compatibility
      const { data, error } = await supabase.functions.invoke(endpoint, {
        body: { 
          action: 'fetchCsvFromUrl',
          params: {
            page,
            itemsPerPage,
            search,
            lang // Add language parameter
          }
        }
      });

      console.log("🔍 MULTILINGUAL CHECK:", { 
        requestedLang: lang,
        firstProductName: data?.data?.[0] ? {
          DE: data.data[0]['ItemDescription.name.DE'],
          EN: data.data[0]['ItemDescription.name.EN']
        } : null
      });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error without data", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      if (error) {
        logger.warn("PlentyOneApiService: Edge function returned error but also data:", error);
      }

      if (!data) {
        throw new Error("No data received from Edge Function");
      }
      
      // Both APIs return the same format now - just process the CSV data
      // For regular CSV fetch, set the entire CSV data and then paginate
      logger.debug("PlentyOneApiService: Setting CSV data...");
      await csvProductService.setCsvData(data.data);
      return await csvProductService.getProducts({ page, itemsPerPage, search, categoryIds });

    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch products from API", error);
      throw error;
    }
  }

  async createOrder(orderData: unknown): Promise<unknown> {
    try {
      logger.debug("Creating order via edge function:", orderData);
      const orderDataTyped = orderData as ErpOrderRequest;
      logger.debug("CustomerInfo details:", {
        hasPaymentMethodType: !!orderDataTyped.customerInfo?.payment_method_type,
        paymentMethodType: orderDataTyped.customerInfo?.payment_method_type,
        selectedPaymentMethod: orderDataTyped.customerInfo?.selectedPaymentMethod,
        customerInfo: orderDataTyped.customerInfo
      });
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { action: 'createOrder', data: orderData }
      });

      if (error) {
        logger.error("Edge function error during order creation", error);
        throw error;
      }

      return data;
    } catch (error) {
      logger.error("Order creation error", error);
      // エラーを適切に伝播させる
      throw error;
    }
  }

  async getOrders(params: GetOrdersParams = {}): Promise<GetOrdersResponse> {
    const { customerId, contactId, page = 1, itemsPerPage = 20, search = "" } = params;
    
    try {
      logger.debug("PlentyOneApiService: Getting orders with params:", {
        customerId,
        contactId,
        page,
        itemsPerPage,
        search
      });
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'getOrders',
          params: {
            customerId,
            contactId,
            page,
            itemsPerPage,
            search
          }
        }
      });

      logger.debug("PlentyOneApiService: Orders API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error without data", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      if (error) {
        logger.warn("PlentyOneApiService: Edge function returned error but also data:", error);
      }

      if (!data) {
        logger.error("PlentyOneApiService: No data received from edge function");
        throw new Error("No data received from API");
      }

      return data as GetOrdersResponse;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch orders from API", error);
      logger.debug("PlentyOneApiService: Falling back to mock orders data...");
      
      // モックデータでのフォールバック
      const mockOrders: OrderSummary[] = [
        {
          id: "ORDER-2024001",
          orderNumber: "ORDER-2024001",
          date: "2024-01-15T10:00:00Z",
          status: "delivered",
          totalAmount: 299.99,
          itemCount: 3,
          items: [
            { name: "Business Socks", quantity: 2, sku: "BS-001" },
            { name: "Premium Tights", quantity: 1, sku: "PT-001" }
          ],
          customerId
        },
        {
          id: "ORDER-2024002",
          orderNumber: "ORDER-2024002", 
          date: "2024-01-10T14:30:00Z",
          status: "shipped",
          totalAmount: 159.50,
          itemCount: 2,
          items: [
            { name: "Comfort Socks", quantity: 3, sku: "CS-001" }
          ],
          customerId
        },
        {
          id: "ORDER-2024003",
          orderNumber: "ORDER-2024003",
          date: "2024-01-05T09:15:00Z", 
          status: "confirmed",
          totalAmount: 89.99,
          itemCount: 1,
          items: [
            { name: "Classic Tights", quantity: 2, sku: "CT-001" }
          ],
          customerId
        }
      ];

      const filteredOrders = search 
        ? mockOrders.filter(order => 
            order.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
            order.items.some(item => 
              item.name.toLowerCase().includes(search.toLowerCase()) ||
              item.sku.toLowerCase().includes(search.toLowerCase())
            )
          )
        : mockOrders;

      const total = filteredOrders.length;
      const totalPages = Math.ceil(total / itemsPerPage);
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const orders = filteredOrders.slice(startIndex, endIndex);

      return {
        orders,
        total,
        currentPage: page,
        totalPages
      };
    }
  }

  async getOrderDetails(orderId: string): Promise<OrderDetails | null> {
    try {
      logger.debug("PlentyOneApiService: Getting order details for:", orderId);
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'getOrderDetails',
          params: {
            orderId
          }
        }
      });

      logger.debug("PlentyOneApiService: Order details API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        if (error.message?.includes('Order not found') || error.details?.includes('404')) {
          return null;
        }
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      if (!data) {
        logger.debug("PlentyOneApiService: No data received, order not found");
        return null;
      }

      return data as OrderDetails;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch order details from API", error);
      logger.debug("PlentyOneApiService: Falling back to mock order details...");
      
      // モックデータでのフォールバック
      const mockOrderDetails: Record<string, OrderDetails> = {
        "ORDER-2024001": {
          id: "ORDER-2024001",
          orderNumber: "ORDER-2024001",
          date: "2024-01-15T10:00:00Z",
          status: "delivered",
          totalAmount: 299.99,
          customerId: "123",
          shippingAddress: {
            company: "ABC Trading GmbH",
            contactPerson: "Hans Müller",
            address1: "Hauptstraße 123",
            address2: "",
            postalCode: "10115",
            city: "Berlin",
            country: "Deutschland"
          },
          items: [
            {
              name: "Business Socks",
              sku: "BS-001",
              quantity: 2,
              unitPrice: 149.99,
              totalPrice: 299.98,
              color: "Schwarz",
              size: "M"
            }
          ],
          estimatedDelivery: "2024-01-22T10:00:00Z",
          deliveredDate: "2024-01-20T14:30:00Z"
        }
      };

      return mockOrderDetails[orderId] || null;
    }
  }

  async findContactByEmail(email: string): Promise<unknown> {
    try {
      logger.debug("PlentyOneApiService: Searching contact by email:", email);
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'findContactByEmail',
          params: {
            email
          }
        }
      });

      logger.debug("PlentyOneApiService: Contact search API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      return data; // nullの場合もそのまま返す（見つからなかった場合）
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to search contact by email", error);
      throw error;
    }
  }

  async getContactInfo(contactId: number): Promise<unknown> {
    try {
      logger.debug("PlentyOneApiService: Getting contact info for:", contactId);
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'getContactInfo',
          params: {
            contactId
          }
        }
      });

      logger.debug("PlentyOneApiService: Contact info API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      if (!data) {
        logger.error("PlentyOneApiService: No data received from edge function");
        throw new Error("No data received from API");
      }

      return data;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch contact info", error);
      throw error;
    }
  }

  async getContactClasses(): Promise<unknown[]> {
    try {
      logger.debug("PlentyOneApiService: Getting contact classes");
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'getContactClasses',
          params: {}
        }
      });

      logger.debug("PlentyOneApiService: Contact classes API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      if (!data) {
        logger.error("PlentyOneApiService: No data received from edge function");
        return [];
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch contact classes", error);
      return [];
    }
  }

  async getContactClass(contactClassId: number): Promise<unknown> {
    try {
      logger.debug("PlentyOneApiService: Getting contact class for:", contactClassId);
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'getContactClass',
          params: {
            contactClassId
          }
        }
      });

      logger.debug("PlentyOneApiService: Contact class API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      return data || null;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch contact class", error);
      return null;
    }
  }

  async fetchCsvFromUrl(csvUrl?: string, forceRefresh: boolean = false): Promise<CsvFetchResponse> {
    try {
      logger.debug("PlentyOneApiService: Fetching CSV from URL:", { csvUrl: csvUrl ? `URL starts with ${csvUrl.substring(0,30)}...` : 'URL from env', forceRefresh });
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'fetchCsvFromUrl',
          params: {
            ...(csvUrl && { csvUrl }),
            forceRefresh
          }
        }
      });

      logger.debug("PlentyOneApiService: CSV fetch API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      if (!data) {
        logger.error("PlentyOneApiService: No data received from edge function");
        throw new Error("No data received from API");
      }

      return data as CsvFetchResponse;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch CSV from URL", error);
      throw error;
    }
  }

  async getProductDescription(productId: string, language?: string): Promise<{ descriptions: Record<string, string>; defaultDescription: string } | null> {
    try {
      logger.debug("PlentyOneApiService: Getting product description for:", productId, "language:", language);
      
      // Check if we should use cache API
      const useCache = import.meta.env.VITE_USE_PRODUCT_CACHE === 'true';
      const functionName = useCache ? 'products-cache-api' : 'plenty-one-api';
      
      logger.debug(`PlentyOneApiService: Using ${functionName} for product description`);
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { 
          action: 'getProductDescription',
          ...(useCache ? { productId, language } : { params: { productId, language } })
        }
      });

      logger.debug("PlentyOneApiService: Product description API response received", { hasData: !!data, hasError: !!error });

      if (error && !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }

      return data || null;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to fetch product description", error);
      // Return null instead of throwing to allow graceful fallback
      return null;
    }
  }

  async updateShippingAddress(contactId: number, addressId: number | undefined, addressData: unknown): Promise<unknown> {
    try {
      logger.debug("PlentyOneApiService: Updating shipping address for contact:", contactId, "addressId:", addressId);

      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: {
          action: 'updateShippingAddress',
          params: {
            contactId,
            addressId  // Pass addressId for update, undefined for new address
          },
          data: addressData
        }
      });

      logger.debug("PlentyOneApiService: Address update API response received", { hasData: !!data, hasError: !!error });

      if (error || !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        logger.error("PlentyOneApiService: Response data issue", { hasData: !!data });
        
        // Try to extract more detailed error information
        let errorMessage = 'Unknown error';
        if (error?.message) {
          errorMessage = error.message;
        }
        if (data?.error) {
          errorMessage = data.error;
        }
        if (data?.details) {
          errorMessage += ` - Details: ${data.details}`;
        }
        
        throw new Error(`Edge function error: ${errorMessage}`);
      }

      if (!data) {
        logger.error("PlentyOneApiService: No data received from edge function");
        throw new Error("No data received from API");
      }

      return data;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to update shipping address", error);
      throw error;
    }
  }

  async getCountries(): Promise<unknown[]> {
    try {
      logger.debug("PlentyOneApiService: Getting countries list");
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'getCountries'
        }
      });

      logger.debug("PlentyOneApiService: Countries API response received", { hasData: !!data, hasError: !!error });

      if (error || !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        logger.error("PlentyOneApiService: Response data issue", { hasData: !!data });
        
        // Return fallback countries if API fails - using correct plentymarkets country IDs
        return [
          { id: 1, name: 'Germany', isoCode2: 'DE' },
          { id: 2, name: 'Austria', isoCode2: 'AT' },
          { id: 4, name: 'Switzerland', isoCode2: 'CH' },
          { id: 12, name: 'United Kingdom', isoCode2: 'GB' },
          { id: 21, name: 'Netherlands', isoCode2: 'NL' },
          { id: 32, name: 'Japan', isoCode2: 'JP' },
        ];
      }

      if (!data) {
        logger.error("PlentyOneApiService: No countries data received");
        throw new Error("No countries data received from API");
      }

      return data;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to get countries", error);
      
      // Return fallback countries - using correct plentymarkets country IDs
      return [
        { id: 1, name: 'Germany', isoCode2: 'DE' },
        { id: 2, name: 'Austria', isoCode2: 'AT' },
        { id: 4, name: 'Switzerland', isoCode2: 'CH' },
        { id: 12, name: 'United Kingdom', isoCode2: 'GB' },
        { id: 21, name: 'Netherlands', isoCode2: 'NL' },
        { id: 32, name: 'Japan', isoCode2: 'JP' },
      ];
    }
  }

  async getOrderSummary(contactId: number): Promise<unknown> {
    try {
      logger.debug("PlentyOneApiService: Getting order summary for contact:", contactId);
      
      const { data, error } = await supabase.functions.invoke('plenty-one-api', {
        body: { 
          action: 'getOrderSummary',
          params: {
            contactId
          }
        }
      });

      logger.debug("PlentyOneApiService: Order summary API response received", { hasData: !!data, hasError: !!error });

      if (error || !data) {
        logger.error("PlentyOneApiService: Edge function error", error);
        logger.error("PlentyOneApiService: Response data issue", { hasData: !!data });
        
        // Return fallback empty summary
        return {
          id: 0,
          addressId: 0,
          contactId: contactId,
          unpaidOrdersCount: 0,
          unpaidOrderTotalAmount: 0,
          orderCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }

      return data;
    } catch (error) {
      logger.error("PlentyOneApiService: Failed to get order summary", error);
      
      // Return fallback empty summary
      return {
        id: 0,
        addressId: 0,
        contactId: contactId,
        unpaidOrdersCount: 0,
        unpaidOrderTotalAmount: 0,
        orderCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }
  }
}

export const plentyOneApi = new PlentyOneApiService();
